/**
 * One client analysis's page — `/audits/:id/analyses/:number` (e.g. `/26`).
 *
 * The analysis is one entry however many reports implement it. Each report
 * is shown in its own tab with the full result view (result, supporting
 * rows). An analysis on hold says why instead.
 *
 * `/analyses/:slug` (one report on its own) shares the route: `AnalysisRoute`
 * sends a number here and anything else to `AnalysisDetail`.
 */

import { useModule } from '../modules/useModule';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { Segmented } from '../shared/Segmented';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useMutation } from '@tanstack/react-query';
import {
	downloadClientAnalysisExcel,
	useAnalysisOverviewQuery,
	useRunAnalysesMutation,
} from '../../queries/analysis';
import { useAuth } from '../../provider/auth';
import { useToast } from '../../provider/toast';
import { PageHeader } from '../../provider/pageTitle';
import { EmptyState, ErrorState, LoadingState } from '../shared/states';
import { useAuditContext } from '../audits/AuditWorkspace';
import { ListStatusBadge } from './ListStatusBadge';
import AnalysisDetail, { AnalysisDetailView } from './AnalysisDetail';
import { dateTime, durationLabel } from '../../utils/format';
import { analysisRows } from './analysisRows';
import { AnalysisInfo } from './AnalysisInfo';
import { ExportAnalysisDialog } from './ExportAnalysisDialog';
import { auditPath } from '../modules/flow';

const LOCKED_REASON =
	'The report has been issued, so analyses cannot be re-run.';

/** `/analyses/26` is an analysis; `/analyses/<slug>` is one report on its
 *  own (a link from an exception). Slugs are never all digits. */
export function AnalysisRoute() {
	const { analysisSlug = '' } = useParams();
	return /^\d+$/.test(analysisSlug) ? (
		<AnalysisPage analysisNumber={analysisSlug} />
	) : (
		<AnalysisDetail />
	);
}

export default function AnalysisPage({
	analysisNumber,
}: {
	analysisNumber: string;
}) {
	const { audit, crumbs: auditCrumbs } = useAuditContext();
	const { link } = useModule();
	const { permissions } = useAuth();
	const canRun = Boolean(permissions?.runAnalyses);
	const toast = useToast();
	const overview = useAnalysisOverviewQuery(audit.pkAuditId);
	const run = useRunAnalysesMutation(audit.pkAuditId);
	const [reportIndex, setReportIndex] = useState(0);
	const [exporting, setExporting] = useState(false);
	const navigate = useNavigate();
	/* Back to wherever the user came from; the Analyses list when the page was
     opened directly (nothing in this app's history to go back to). */
	const goBack = () =>
		window.history.state?.idx > 0
			? navigate(-1)
			: navigate(link(`/audits/${audit.pkAuditId}/analyses`));
	const backButton = (
		<IconButton
			size="small"
			onClick={goBack}
			aria-label="Back"
			title="Back"
			sx={{ mr: 0.5 }}
		>
			<ArrowBackIcon fontSize="small" />
		</IconButton>
	);
	const download = useMutation({
		mutationFn: (analysisNumber: number) =>
			downloadClientAnalysisExcel(audit.pkAuditId, analysisNumber),
		onError: (error) =>
			toast(
				'danger',
				error instanceof Error ? error.message : 'The download failed.',
			),
	});

	const crumbs = [
		...auditCrumbs,
		{ label: 'Analyses', to: `${auditPath(audit)}/analyses` },
	];

	if (overview.isPending)
		return <LoadingState label="Loading the analysis…" />;
	if (overview.error)
		return <ErrorState error={overview.error} onRetry={overview.refetch} />;
	if (!overview.data) return null;

	const entry = analysisRows(overview.data.rows, overview.data.analyses).find(
		(candidate) => String(candidate.number) === analysisNumber,
	);
	if (!entry) {
		return (
			<EmptyState title="Analysis not found">
				This audit has no analysis {analysisNumber}.
			</EmptyState>
		);
	}

	const title = `${entry.number}. ${entry.title}`;
	if (entry.rollup === null) {
		return (
			<Stack gap="var(--s4)" sx={{ minWidth: 0 }}>
				<PageHeader
					breadcrumbs={crumbs}
					kind={backButton}
					title={title}
					subtitle="On hold"
				/>
				<EmptyState title="On hold">{entry.onHold}</EmptyState>
			</Stack>
		);
	}

	const { rollup, reports } = entry;
	const selectedIndex = Math.min(reportIndex, reports.length - 1);
	const selected = reports[selectedIndex];
	const locked = overview.data.locked;
	const busy = rollup.status === 'RUNNING' || rollup.status === 'QUEUED';
	const allBlocked = rollup.availability === 'BLOCKED';

	/* "Started" and "completed" are announced by the app-wide run watcher. */
	const runAll = () =>
		run.mutate(
			reports.map((report) => report.row.slug),
			{
				onError: (error) =>
					toast(
						'danger',
						error instanceof Error
							? error.message
							: 'Could not start.',
					),
			},
		);

	return (
		<Stack gap="var(--s4)" sx={{ minWidth: 0 }}>
			<PageHeader
				breadcrumbs={crumbs}
				kind={backButton}
				title={title}
				subtitle={
					<Stack
						component="span"
						direction="row"
						spacing={1}
						alignItems="center"
						flexWrap="wrap"
					>
						<AnalysisInfo description={entry.description} />
						<ListStatusBadge row={rollup} running={run.isPending} />
						{rollup.completedAt && (
							<Typography component="span" variant="inherit">
								Last run {dateTime(rollup.completedAt)}
							</Typography>
						)}
						{rollup.durationMs !== null && (
							<Typography component="span" variant="inherit">
								Duration {durationLabel(rollup.durationMs)}
							</Typography>
						)}
					</Stack>
				}
				actions={
					<Box display="flex" gap="var(--s2)">
						{canRun && !allBlocked && (
							<Button
								variant="outlined"
								loading={run.isPending}
								disabled={locked || busy || run.isPending}
								title={
									locked
										? LOCKED_REASON
										: busy
											? 'This analysis is running.'
											: undefined
								}
								onClick={runAll}
							>
								{rollup.status === 'NOT_RUN'
									? 'Run'
									: rollup.status === 'FAILED'
										? 'Retry'
										: 'Re-run'}
							</Button>
						)}
						<Button
							variant="outlined"
							disabled={rollup.status !== 'COMPLETED'}
							title={
								rollup.status !== 'COMPLETED'
									? 'Available once the analysis has completed.'
									: undefined
							}
							onClick={() => setExporting(true)}
						>
							Export…
						</Button>
						<Button
							variant="contained"
							loading={download.isPending}
							disabled={
								rollup.status !== 'COMPLETED' ||
								download.isPending
							}
							title={
								rollup.status !== 'COMPLETED'
									? 'Every report must complete before the Excel can be downloaded.'
									: reports.length > 1
										? 'One workbook with every report of this analysis, a sheet each.'
										: undefined
							}
							onClick={() => download.mutate(entry.number)}
						>
							Download Excel
						</Button>
					</Box>
				}
			/>

			{reports.length > 1 && (
				<Segmented
					value={selectedIndex}
					onChange={setReportIndex}
					ariaLabel="Reports of this analysis"
					options={reports.map((report, index) => ({
						value: index,
						label: `Report ${report.index}: ${report.label}`,
					}))}
				/>
			)}

			{selected && (
				<AnalysisDetailView
					key={selected.row.slug}
					slug={selected.row.slug}
					embedded
				/>
			)}

			<ExportAnalysisDialog
				open={exporting}
				onClose={() => setExporting(false)}
				auditId={audit.pkAuditId}
				entry={entry}
			/>
		</Stack>
	);
}
